import { HttpParams } from '@angular/common/http';
import { Injectable, LOCALE_ID, Inject } from '@angular/core';
import { Observable, of, pipe } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../api/api.service';
import {ConstantsService} from "../constants/constants.service";
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import { FilterEntry } from 'src/app/pages/questionnaires/questionnaires.types';

@Injectable({
    providedIn: 'root',
})
export class QuestionnaireService {
    static survey_type_mapping = {
        'exit': 1,
        'pulse': 2,
        'reference': 3,
    };
    static category_map = {
        'candidate': 29,
        'academic': 24,
        'voluntary': 23,
        'exit': 28,
        'employment': 17,
        'character':27,
    }
    static access_mapping = {
      'Restricted': 0,
      'Everyone': 1,
    };
    path = "questionnaire/";

    constructor(
      private apiService: ApiService,
      private permissions: PermissionsService,
      @Inject(LOCALE_ID) public locale: string
    ) {
      this.locale = this.locale.toLowerCase();
    }

    getMetrics(metric_type="exit"): Observable<any> {
        let params = new HttpParams();
        if (metric_type){
            params = params.set('metric_type', metric_type)
        }
        const path = this.path +'language/'+ this.locale + '/metrics';
        return this.apiService.get(path, {params});
    }

    getQuestionsMetrics(metrics: string, metric_type: string | null): Observable<any> {
      let params = new HttpParams();
      if (metric_type){
        params = params.set('metric_type', metric_type)
      }
      if (metrics){
        params = params.set('metrics', metrics)
      }
      const path = this.path+'organisation/language/'+this.locale+'/metric/questions';
      return this.apiService.get(path, {params});
    }

    validateQuestionnaireTitle(questionnaireTitle: string, questionnaireId?: string): any {
        console.log(questionnaireId)
        const path: string = this.path + 'validate';
        let params: HttpParams = new HttpParams().set('name', questionnaireTitle);
        if (questionnaireId) {
            params = params.set('id', questionnaireId);
        }
        return this.apiService.get(path, {params});
    }

    validateQuestionnaireTitleDeprecated(questionnaireTitle: string): any {
        const path = this.path+'validate';
        const params = new HttpParams().set('name', questionnaireTitle);
        return this.apiService.get(path, {params});
    }

    getQuestionnaires(searchOptions?: any, rowsPerView = 0, pageNumber = 0, sortString?: string, is_default?:any, surveyType?:any, active?: boolean): Observable<any> {
        let params = new HttpParams();
        if (rowsPerView != 0){
            params = params.set('limit', String(rowsPerView))
            .set('offset', String(rowsPerView*(pageNumber-1)));
        }
        if (sortString){
            params = params.set('sort',sortString);
        }
        if (searchOptions) {
            searchOptions['columns'].forEach(element => {
                params = params.set(element, searchOptions['value'])
            });
        }
        if (is_default){
            params = params.set('is_default', is_default);
        }

        if(surveyType){
            params = params.set('survey_type', surveyType);
        }

        if (active) {
            params = params.set('active', active);
        }

        const path = this.path+'organisation/questionnaire/all';
        return this.apiService.get(path, {params});
    }

  getQuestionnairesDuplicateParams(searchOptions?: any, rowsPerView = 0, pageNumber = 0, sortString?: string, is_default?: any, survey_type?: any, refine_by?: any, roleAndPermissionFF?: any, filterOptions?: FilterEntry[]): Observable<any> {
      let params = new HttpParams();
      let path = ''
      if (rowsPerView != 0){
        params = params.set('limit', String(rowsPerView))
          .set('offset', String(rowsPerView*(pageNumber-1)));
      }
      if (sortString){
        params = params.set('sort',sortString);
      }
      if (searchOptions) {
        searchOptions.forEach(element => {
          params = params.append(element.key, element.value);
        });
      }
      if (is_default!=undefined){
        params = params.set('is_default', is_default);
      }
      if (survey_type){
        params = params.set('survey_type', survey_type);
      }
      if (refine_by) {
        params = params.append('refine_by', refine_by);
      }
      if (filterOptions) {
        filterOptions.forEach(option => {
          if (option.value !== null) {
            if (Array.isArray(option.value)) {
              option.value.forEach(val => {
                params = params.append(option.key, val);
              });
            } else {
              params = params.set(option.key, option.value);
            }
          }
        });
      }
      if (roleAndPermissionFF) {
        path = this.path + 'organisation/survey/all';
      } else {
        path = this.path + 'organisation/questionnaire/all';
      }
      return this.apiService.get(path, {params});
    }
    getQuestionnaire(questionnaireId: string, isPreview: boolean = false): Observable<any> {
        let is_preview = isPreview ? '1' : '0'
        const path = this.path+'organisation/questionnaire/'+questionnaireId+'/questions?is_preview=' + is_preview;
        return this.apiService.get(path);
    }

    getQuestionnairePackage(packageId: string): Observable<any> {
        const path = this.path+'organisation/questionnaire/package/'+packageId+'/';
        return this.apiService.get(path);
    }

    getQuestions(searchText="", metrics="", checkbox=[], sortOptions="", survey_type="", group=""): Observable<any> {
        const path = this.path+'organisation/language/'+this.locale+'/questions/all';
        let searchParams = new HttpParams();

        if (searchText){
            searchParams = searchParams.set('text',searchText);
        }
        if (metrics){
            searchParams = searchParams.set('metrics', metrics);
        }
        if (checkbox.length){
            checkbox.forEach(element=>{
                searchParams = searchParams.set(element, 'true');
            })
        }
        if (sortOptions){
            searchParams = searchParams.set('sort', sortOptions);
        }
        if (survey_type){
            searchParams = searchParams.set('survey_type', survey_type)
        }
        if (group && group!='All'){
            searchParams = searchParams.set('groups', group);
        }
        return this.apiService.get(path, {'params': searchParams});
    }

    postQuestionnaire(
        questionnaireBody:any,
        survey_type = "exit",
        questionnaireId:string = '',
        is_additional_comments_enabled=true,
        is_opt_in_question_enabled=true,
        opt_in_question=ConstantsService.DEFAULT_OPT_IN_QUESTION,
        min_referee=ConstantsService.DEFAULT_REFEREE_COUNT,
        noteToCandidate='',
        noteToReferee='',
    ): Observable<any> {
        // Record the questionnaireID param in postQuestionnaire for audit logs if it's duplication process
        questionnaireBody['survey_type'] = survey_type;
        questionnaireBody['is_additional_comments_enabled']=is_additional_comments_enabled;
        if (survey_type == 'reference'){
            questionnaireBody['is_opt_in_question_enabled'] = is_opt_in_question_enabled;
            questionnaireBody['opt_in_question'] = opt_in_question;
            questionnaireBody['min_referee'] = min_referee;
            questionnaireBody['note_to_candidate'] = noteToCandidate;
            questionnaireBody['note_to_referee'] = noteToReferee;
        }

        const path = this.path+'create?parent_questionnaire_id=' + questionnaireId;
        return this.apiService.post(path, questionnaireBody);
    }

    putQuestionnaire(
        questionnaireId: string,
        questionnaireBody:any,
        surveyType: string,
        is_additional_comments_enabled=true,
        is_opt_in_question_enabled=true,
        opt_in_question=ConstantsService.DEFAULT_OPT_IN_QUESTION,
        min_referee=ConstantsService.DEFAULT_REFEREE_COUNT,
        noteToCandidate='',
        noteToReferee='',
    ): Observable<any> {
        questionnaireBody['survey_type'] = surveyType;
        questionnaireBody['is_additional_comments_enabled']=is_additional_comments_enabled;
        if (surveyType == 'reference'){
            questionnaireBody['is_opt_in_question_enabled'] = is_opt_in_question_enabled;
            questionnaireBody['opt_in_question'] = opt_in_question;
            questionnaireBody['min_referee'] = min_referee;
            questionnaireBody['note_to_candidate'] = noteToCandidate;
            questionnaireBody['note_to_referee'] = noteToReferee;
        }
        const path = this.path+questionnaireId+'/edit';
        return this.apiService.put(path, questionnaireBody);
    }

    putQuestionnaireTitle(questionnaireBody:any) {
        const path = this.path + 'title/update'
        return this.apiService.put(path, questionnaireBody);
    }

    putQuestionnaireAccess(questionnaireId: string, questionnaireBody:any) {
        const path = this.path + questionnaireId + '/access/update'
        return this.apiService.put(path, questionnaireBody);
    }

    putQuestionnairePermissionAccess(questionnaireId: string, questionnaireBody:any) {
        const path = this.path + questionnaireId + '/access/permission/update'
        return this.apiService.put(path, questionnaireBody);
    }

    deleteQuestionnaire(questionnaireId: string): Observable<any>{
        const path = this.path+questionnaireId+'/delete';
        return this.apiService.delete(path);
    }

    getUsersQuestionnaire(questionnaireId: string, searchOptions, filter: string[], rowsPerView, pageNumber, sortString): Observable<any>{
        let params = new HttpParams()
        .set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView*(pageNumber-1)));
        if (sortString){
            params = params.set('sort', sortString.join(','));
        }
        if (searchOptions) {
            searchOptions.forEach(element => {
                params = params.set(element.key, element.value)
            });
        }
        if (filter.length!=0){
            params = params.set('filter', filter.join(','));
        }
        const path = this.path+questionnaireId+'/users';
        return this.apiService.get(path, {params});
    }

    getUsersWithSurveyPermissions(questionnaireId: string, searchOptions, filter: any, rowsPerView, pageNumber, sortString): Observable<any>{
      let params = new HttpParams()
        .set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView*(pageNumber-1)));
        if (sortString){
            params = params.set('sort', sortString.join(','));
        }
        if (searchOptions) {
            searchOptions.forEach(element => {
                params = params.append(element.key, element.value);
            });
        }
        if (filter.length!=0){
            params = params.set('filter', filter);
        }
        const path = this.path +questionnaireId + '/users/permissions';
        return this.apiService.get(path, {params});
    }

    updateUsersQuestionnaire(questionnaireId: string, users: any){
        const path = this.path+questionnaireId+'/users';
        return this.apiService.post(path, users);
    }

    updateQuestionnaireUsersPermission(questionnaireId: string, users: any) {
      const path = this.path + questionnaireId + '/users/permissions';
      return this.apiService.post(path, users);
    }

    listQuestionnaires(count?, limit?, surveyType?): Observable<any> {
        let params = new HttpParams()
        if(count) {
          params = params.set('response_count', count);
        }
        if(limit) {
          params = params.set('limit', limit).set('offset', String(0));
        }
        if(surveyType){
        params = params.set('survey_type', surveyType)
        }
        return this.apiService.get('questionnaire/organisation/questionnaire/all', {params}).pipe(map(data => data['data']));
      }

    getNewQuestionnaireTitle(baseTitle){
        let params = new HttpParams().set('title', baseTitle);
        const path = this.path+'organisation/questionnaire/all';
        return this.apiService.get(path, {params}).pipe(map(data=>data['data']));
    }

    postQuestion(questionBody){
        const path = this.path+'organisation/questions/create';
        questionBody['language']=this.locale;
        return this.apiService.post(path, questionBody).pipe(map(data=>data['data']));
    }
    editQuestion(questionId, questionBody){
        const path = this.path+'organisation/questions/'+questionId+'/edit';
        return this.apiService.put(path, questionBody).pipe(map(data=>data['data']));
    }
    deleteQuestion(questionId){
        const path = this.path+'organisation/questions/'+questionId+'/delete';
        return this.apiService.delete(path).pipe(map(data=>data['data']));
    }
    hideQuestion(questionId){
        const path = this.path+'organisation/questions/'+questionId+'/edit';
        return this.apiService.put(path,{'is_hidden':true}).pipe(map(data=>data['data']));
    }
    getConfirmationQuestion(){
        const params = new HttpParams().set('type', 'candidate');
        const path = this.path +'organisation/language/'+ this.locale + '/confirmation/question';
        return this.apiService.get(path, {params});
    }
    getFilterConfig(){
        const path = this.path +'organisation/language/'+ this.locale + '/reference-group-details';
        return this.apiService.get(path);
    }

    getUserQuestionnairePermissions(checkId){
      const path = `${this.path}check/${checkId}/user-permissions`;
      return this.apiService.get(path).pipe(map(res => res));
    }

    activateSurvey(surveyId: number, activate: boolean) {
      const path = `${this.path}${surveyId}/activate`;
      return this.apiService.post(path, {'active': activate});
    }
}
